import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { syncZuba } from "@/lib/zuba.functions";
import { ZubaColorMap } from "@/components/admin/ZubaColorMap";

import {
  STATUS_CLASS,
  STATUS_DOT,
  STATUS_LABEL,
  STATUS_ORDER,
  floorSortKey,
  type UnitStatus,
} from "@/lib/zuba-shared";

export const Route = createFileRoute("/_authenticated/admin/zuba")({
  component: ZubaDashboard,
});

type ProjectRow = {
  id: string;
  name: string;
  location: string | null;
  sheet_url: string | null;
  is_active: boolean;
  sort_order: number;
};

type UnitRow = {
  id: string;
  project_id: string;
  tower: string | null;
  floor: string | null;
  unit_code: string;
  typology: string | null;
  area_m2: number | null;
  status: string;
  last_synced_at: string;
};

type LogRow = {
  project_id: string | null;
  status: string;
  units_synced: number;
  error_message: string | null;
  triggered_by: string;
  finished_at: string | null;
};

async function fetchAll() {
  const [projects, units, logs] = await Promise.all([
    supabase.from("zuba_projects").select("*").order("sort_order"),
    supabase
      .from("zuba_units")
      .select("id,project_id,tower,floor,unit_code,typology,area_m2,status,last_synced_at")
      .limit(20000),
    supabase
      .from("zuba_sync_log")
      .select("project_id,status,units_synced,error_message,triggered_by,finished_at")
      .order("started_at", { ascending: false })
      .limit(200),
  ]);
  if (projects.error) throw projects.error;
  if (units.error) throw units.error;
  return {
    projects: (projects.data ?? []) as ProjectRow[],
    units: (units.data ?? []) as UnitRow[],
    logs: (logs.data ?? []) as LogRow[],
  };
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-foreground/75 hover:bg-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function ZubaDashboard() {
  const qc = useQueryClient();
  const runSync = useServerFn(syncZuba);
  const [syncing, setSyncing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["zuba", "all"],
    queryFn: fetchAll,
    refetchInterval: 5 * 60 * 1000,
  });

  const [q, setQ] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<UnitStatus[]>([]);
  const [typology, setTypology] = useState("");
  const [floor, setFloor] = useState("");
  const [minM2, setMinM2] = useState("");
  const [maxM2, setMaxM2] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const projects = data?.projects ?? [];
  const units = data?.units ?? [];

  const typologies = useMemo(
    () => Array.from(new Set(units.map((u) => u.typology).filter(Boolean) as string[])).sort(),
    [units],
  );
  const floors = useMemo(
    () =>
      Array.from(new Set(units.map((u) => u.floor).filter(Boolean) as string[])).sort(
        (a, b) => floorSortKey(a) - floorSortKey(b),
      ),
    [units],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const min = minM2 ? Number(minM2) : null;
    const max = maxM2 ? Number(maxM2) : null;
    const nameById = new Map(projects.map((p) => [p.id, p.name]));
    return units.filter((u) => {
      if (projectIds.length && !projectIds.includes(u.project_id)) return false;
      if (statuses.length && !statuses.includes(u.status as UnitStatus)) return false;
      if (typology && u.typology !== typology) return false;
      if (floor && u.floor !== floor) return false;
      if (min !== null && (u.area_m2 ?? 0) < min) return false;
      if (max !== null && (u.area_m2 ?? 0) > max) return false;
      if (term) {
        const hay = `${u.unit_code} ${u.typology ?? ""} ${u.tower ?? ""} ${
          nameById.get(u.project_id) ?? ""
        }`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [units, projects, q, projectIds, statuses, typology, floor, minM2, maxM2]);

  const kpis = useMemo(() => {
    const c = { total: filtered.length, libre: 0, senado: 0, ocupado: 0, desconocido: 0 };
    for (const u of filtered) c[(u.status as UnitStatus) ?? "desconocido"]++;
    return c;
  }, [filtered]);

  const byProject = useMemo(() => {
    const map = new Map<string, UnitRow[]>();
    for (const u of filtered) {
      const arr = map.get(u.project_id) ?? [];
      arr.push(u);
      map.set(u.project_id, arr);
    }
    return map;
  }, [filtered]);

  const lastLogByProject = useMemo(() => {
    const map = new Map<string, LogRow>();
    for (const l of data?.logs ?? []) {
      if (l.project_id && !map.has(l.project_id)) map.set(l.project_id, l);
    }
    return map;
  }, [data?.logs]);

  const lastSync = data?.logs?.[0];

  const doSync = async (projectId?: string) => {
    setSyncing(true);
    try {
      const res = (await runSync({ data: projectId ? { projectId } : {} })) as {
        results: Array<{ project: string; ok: boolean; units: number; error?: string }>;
      };
      const ok = res.results.filter((r) => r.ok);
      const bad = res.results.filter((r) => !r.ok);
      toast.success(
        `Sincronización: ${ok.length} proyecto(s) OK · ${ok.reduce((s, r) => s + r.units, 0)} unidades`,
      );
      if (bad.length) toast.error(`${bad.length} proyecto(s) con error: ${bad[0]?.error ?? ""}`);
      await qc.invalidateQueries({ queryKey: ["zuba"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const toggle = <T,>(arr: T[], v: T, set: (v: T[]) => void) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Disponibilidad ZUBA</h1>
          <p className="text-xs text-muted-foreground">
            {lastSync?.finished_at
              ? `Última sincronización: ${new Date(lastSync.finished_at).toLocaleString("es-PY")} · ${lastSync.triggered_by}`
              : "Sin sincronizaciones registradas todavía"}
          </p>
        </div>
        <button
          onClick={() => doSync()}
          disabled={syncing}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Actualizando…" : "Actualizar ahora"}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Unidades", value: kpis.total, dot: "bg-foreground/30" },
          { label: "Libres", value: kpis.libre, dot: STATUS_DOT.libre },
          { label: "Señadas", value: kpis.senado, dot: STATUS_DOT.senado },
          { label: "Ocupadas", value: kpis.ocupado, dot: STATUS_DOT.ocupado },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className={`h-2 w-2 rounded-full ${k.dot}`} /> {k.label}
            </div>
            <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      <ZubaColorMap projects={projects} />

      {/* Filters */}

      <div className="space-y-3 rounded-lg border border-border bg-card p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar unidad, proyecto o tipología…"
            className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {projects.map((p) => (
            <Chip
              key={p.id}
              active={projectIds.includes(p.id)}
              onClick={() => toggle(projectIds, p.id, setProjectIds)}
            >
              {p.name}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_ORDER.map((s) => (
            <Chip key={s} active={statuses.includes(s)} onClick={() => toggle(statuses, s, setStatuses)}>
              <span className="inline-flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${STATUS_DOT[s]}`} /> {STATUS_LABEL[s]}
              </span>
            </Chip>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <select
            value={typology}
            onChange={(e) => setTypology(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Tipología: todas</option>
            {typologies.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Piso: todos</option>
            {floors.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <input
            value={minM2}
            onChange={(e) => setMinM2(e.target.value)}
            inputMode="numeric"
            placeholder="m² mín."
            className="h-9 rounded-md border border-input bg-background px-2 font-mono text-sm"
          />
          <input
            value={maxM2}
            onChange={(e) => setMaxM2(e.target.value)}
            inputMode="numeric"
            placeholder="m² máx."
            className="h-9 rounded-md border border-input bg-background px-2 font-mono text-sm"
          />
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Cargando unidades…</p>}
      {!isLoading && units.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Todavía no hay unidades sincronizadas. Configurá la credencial de Google y presioná
          “Actualizar ahora”.
        </div>
      )}

      {/* Projects */}
      <div className="space-y-3">
        {projects.map((p) => {
          const list = byProject.get(p.id) ?? [];
          if (!list.length && (projectIds.length || q || statuses.length)) return null;
          const isOpen = !collapsed[p.id];
          const log = lastLogByProject.get(p.id);

          const floorsMap = new Map<string, UnitRow[]>();
          for (const u of list) {
            const k = u.floor ?? "Sin piso";
            floorsMap.set(k, [...(floorsMap.get(k) ?? []), u]);
          }
          const floorEntries = Array.from(floorsMap.entries()).sort(
            (a, b) => floorSortKey(b[0]) - floorSortKey(a[0]),
          );
          const free = list.filter((u) => u.status === "libre").length;

          return (
            <section key={p.id} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-2.5">
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [p.id]: isOpen }))}
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-semibold">{p.name}</span>
                  {p.location && (
                    <span className="text-xs text-muted-foreground">· {p.location}</span>
                  )}
                  <span className="font-mono text-xs text-muted-foreground">
                    {free}/{list.length} libres
                  </span>
                </button>

                {log?.status === "error" && (
                  <span
                    title={log.error_message ?? ""}
                    className="inline-flex items-center gap-1 text-xs text-status-taken"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" /> error de sync
                  </span>
                )}
                {p.sheet_url && (
                  <a
                    href={p.sheet_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Hoja
                  </a>
                )}
                <button
                  onClick={() => doSync(p.id)}
                  disabled={syncing}
                  className="rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary disabled:opacity-50"
                >
                  Sincronizar
                </button>
              </div>

              {/* Occupancy strip */}
              {list.length > 0 && (
                <div className="flex h-1.5 w-full">
                  {STATUS_ORDER.map((s) => {
                    const n = list.filter((u) => u.status === s).length;
                    if (!n) return null;
                    return (
                      <div
                        key={s}
                        className={STATUS_DOT[s]}
                        style={{ width: `${(n / list.length) * 100}%` }}
                      />
                    );
                  })}
                </div>
              )}

              {isOpen && (
                <div className="divide-y divide-border">
                  {floorEntries.map(([f, fUnits]) => (
                    <div key={f} className="px-3 py-2.5">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                          {f}
                        </span>
                        <div className="flex h-1 flex-1 overflow-hidden rounded-full bg-muted">
                          {STATUS_ORDER.map((s) => {
                            const n = fUnits.filter((u) => u.status === s).length;
                            if (!n) return null;
                            return (
                              <div
                                key={s}
                                className={STATUS_DOT[s]}
                                style={{ width: `${(n / fUnits.length) * 100}%` }}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
                        {fUnits
                          .sort((a, b) => a.unit_code.localeCompare(b.unit_code, "es", { numeric: true }))
                          .map((u) => (
                            <div
                              key={u.id}
                              className={`rounded-md border px-2.5 py-2 ${
                                STATUS_CLASS[(u.status as UnitStatus) ?? "desconocido"]
                              }`}
                            >
                              <div className="flex items-baseline justify-between gap-2">
                                <span className="font-mono text-sm font-bold tabular-nums">
                                  {u.unit_code}
                                </span>
                                <span className="font-mono text-[11px] tabular-nums opacity-80">
                                  {u.area_m2 ? `${u.area_m2} m²` : "—"}
                                </span>
                              </div>
                              <div className="mt-0.5 truncate text-[11px] opacity-90" title={u.typology ?? ""}>
                                {u.typology ?? "—"}
                              </div>
                              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider">
                                {STATUS_LABEL[(u.status as UnitStatus) ?? "desconocido"]}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
